/**
 * crossGridAccepts — THE cross-grid membership gate (grid 0.8.42; single
 * item-aware predicate since grid 0.9.4).
 *
 * The one authority on where a dragged item may LAND during a cross-grid
 * transfer. Wired into the grid store via `configureInteractions({ accepts })`
 * (usePageNode) AND into the adapter's targeting (`useWebCrossGridDrag({
 * accepts })`, SitemapCanvas / crossTabTargetAt) — 0.9.4 widened the adapter
 * callback to `(gridId, kind, item?)` with the REAL dragged item, so the old
 * kind-level twin (`crossGridKindAccepts`) is gone: one pure function, no
 * drift between the store gate and the targeting filter. Only consulted for
 * CROSS-grid targets — rearranging within a grid never passes through here.
 *
 * Rules:
 *  - PINNED DEFAULTS: the seeded shared-region items (fixed "__<band>-<slug>"
 *    ids: __header-logo, __header-nav, __footer-text) may be rearranged
 *    within their band but never leave it — the user can ADD to the
 *    header/footer, not strip their defaults into the body or another band;
 *  - the shared header/footer bands ("__header"/"__footer") take LEAF widgets
 *    (add an image, a text, a button to the header), never sections;
 *  - a PAGE-BODY grid (top-level page grid, no parent) holds ONLY sections
 *    (contentType "grid") — so a bare widget can never land between sections;
 *  - a SECTION's nested grid holds ONLY leaf widgets — so a section can never
 *    nest inside a section;
 *  - an INLINE ("inline-grid") is a one-row nested grid: it goes into a
 *    section grid or the header/footer band, never the page body or another
 *    Inline. Its own grid takes leaf widgets only, and only while the row has
 *    the free columns for them (the engine has no row cap — this gate is it).
 */
import { useGridStore } from "@fredericrous/grid";
import { FOOTER_GRID_ID, HEADER_GRID_ID } from "./sharedRegions.js";

/** An Inline's row: its column count and the columns its blocks already use. */
export interface InlineRow {
  readonly columns: number;
  readonly used: number;
}

interface RowGridState {
  readonly parentGridId?: string;
  readonly parentItemId?: string;
  readonly config: { readonly columns: number };
  readonly items: Readonly<
    Record<
      string,
      {
        readonly contentType?: string;
        readonly position: { readonly w: number };
      }
    >
  >;
}

/** The row an Inline's grid is, or undefined for any other grid. Reads the
 *  parent stamp usePageNode's reconciler puts on every nested grid. */
export function inlineRowIn(
  grids: Readonly<Record<string, RowGridState | undefined>>,
): (gridId: string) => InlineRow | undefined {
  return (gridId) => {
    const g = grids[gridId];
    if (!g?.parentGridId || !g.parentItemId) return undefined;
    const host = grids[g.parentGridId]?.items[g.parentItemId];
    if (host?.contentType !== "inline-grid") return undefined;
    const used = Object.values(g.items).reduce((n, it) => n + it.position.w, 0);
    return { columns: g.config.columns, used };
  };
}

export function crossGridAccepts(
  item: {
    id?: string;
    contentType: string;
    w?: number;
    position?: { w: number };
  },
  toGridId: string,
  parentGridIdOf: (gridId: string) => string | undefined,
  // Defaults to the live store, so every caller (page, cross-tab, the
  // canvas-level cross-grid drag) enforces an Inline's one-row capacity.
  inlineRowOf: (gridId: string) => InlineRow | undefined = (gid) =>
    inlineRowIn(useGridStore.getState().grids)(gid),
): boolean {
  // Pinned defaults: "__header-logo" → home band "__header", etc. Since this
  // gate only sees CROSS-grid targets, anything other than the home band is
  // a refusal (rearranging inside the band never consults the gate).
  const id = item.id ?? "";
  if (id.startsWith("__")) {
    const home = "__" + id.slice(2).split("-")[0];
    return toGridId === home;
  }

  const itemIsSection = item.contentType === "grid";
  const itemIsInline = item.contentType === "inline-grid";
  if (toGridId === HEADER_GRID_ID || toGridId === FOOTER_GRID_ID) {
    return !itemIsSection;
  }
  const row = inlineRowOf(toGridId);
  if (row) {
    if (itemIsSection || itemIsInline) return false;
    const w = Math.min(item.position?.w ?? item.w ?? 1, row.columns);
    return w <= row.columns - row.used;
  }
  const toIsPageBody = !parentGridIdOf(toGridId);
  return toIsPageBody ? itemIsSection : !itemIsSection;
}

/**
 * Adapter fallback when a call site only knows the drag KIND (the item is
 * not resolvable — e.g. a targeting frame before the payload exists): a
 * minimal item whose contentType encodes the section/leaf distinction, which
 * is all the rules above read besides the pinned-default id prefix (pinned
 * defaults always resolve to a real item, so the fallback never masks them).
 */
export function itemForKind(kind: string): { contentType: string } {
  return { contentType: kind === "container" ? "grid" : "app" };
}
